import type { IconProp } from '../icon'

import { isReactText, isString } from '../utils'
import ButtonIcon from './buttonIcon'
import ButtonText from './buttonText'
import { loadingIconProps } from './consts'

/**
 * Unified icon renderer for all button icon positions.
 * Converts string icon names to ButtonIcon components with appropriate margins.
 *
 * @param iconProp - Icon name string or React element
 * @param position - Icon position: 'main' (center), 'left', or 'right'
 * @param isLoading - Whether to show loading spinner (only applies to left position)
 * @returns Rendered icon or null
 */
export const renderButtonIcon = (
  iconProp: IconProp | React.ReactElement | undefined,
  position: 'left' | 'right',
  isLoading: boolean = false,
): React.ReactElement | null => {
  if (position === 'left' && isLoading) {
    return <ButtonIcon {...loadingIconProps} />
  }

  if (!iconProp) return null

  // JSX element icons are returned as-is — no wrapper
  if (!isString(iconProp)) return iconProp as React.ReactElement

  return <ButtonIcon name={iconProp} />
}

/**
 * Renders the button content (text or children).
 * Wraps text strings in ButtonText component with optional truncation.
 *
 * @param children - React children content
 * @param text - Text string to render
 * @param isTruncated - Whether to enable text truncation
 * @returns Rendered content
 */
export const renderContent = (
  children: React.ReactNode,
  text: React.ReactNode,
  isTruncated: boolean,
): React.ReactNode => {
  if (children) return children
  // isReactText covers string/number; the second branch wraps JSX/complex nodes via children
  if (isReactText(text)) return <ButtonText isTruncated={isTruncated} text={text} />
  if (text) return <ButtonText isTruncated={isTruncated}>{text}</ButtonText>
  return null
}
